import React, { JSX } from 'react';
import { WarningTwoIcon } from '@chakra-ui/icons';
import { Card, CardBody, HStack, Text, VStack } from '@chakra-ui/react';

import { useTranslations } from '../../../contexts/LocalizationContext';
import { MetricResult } from '../model/WeeklyStatisticsResponse';
import { formatNumber, NormalizedThreshold } from '../utils/metrics';

interface Props {
  result: MetricResult;
  translationKey: string;
  threshold?: NormalizedThreshold;
}

const MetricTile = ({ result, translationKey, threshold }: Props): JSX.Element => {
  const translations = useTranslations();
  const name = translations(translationKey);
  const alert = result.alert;

  const thresholdUp = alert?.thresholdUp ?? threshold?.thresholdUp;
  const thresholdDown = alert?.thresholdDown ?? threshold?.thresholdDown;

  const alertMessage = alert
    ? alert.direction === 'above'
      ? translations('monitoring_alert_above', { '{{max}}': formatNumber(alert.thresholdUp) })
      : translations('monitoring_alert_below', { '{{min}}': formatNumber(alert.thresholdDown) })
    : undefined;

  const expectedRange =
    thresholdUp != null && thresholdDown != null
      ? translations('monitoring_expected_range', {
          '{{min}}': formatNumber(thresholdDown),
          '{{max}}': formatNumber(thresholdUp),
        })
      : undefined;

  const ariaLabel = alert
    ? `${name}: ${formatNumber(result.value)}, ${alertMessage}`
    : `${name}: ${formatNumber(result.value)}, ${translations('monitoring_within_range')}`;

  return (
    <Card
      borderRadius="xl"
      borderWidth="1px"
      borderColor="grey.20"
      borderLeftWidth={alert ? '4px' : '1px'}
      borderLeftColor={alert ? 'red.500' : 'grey.20'}
      bg={alert ? 'red.50' : 'transparent'}
      role="status"
      aria-label={ariaLabel}
    >
      <CardBody>
        <VStack align="start" spacing={1}>
          <Text fontSize="xs" color={alert ? 'grey.500' : 'grey.200'} fontWeight="medium">
            {name}
          </Text>
          <Text fontSize="3xl" fontWeight="bold" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatNumber(result.value)}
          </Text>
          {expectedRange && (
            <Text fontSize="xs" color={alert ? 'grey.500' : 'grey.200'} textAlign="left">
              {expectedRange}
            </Text>
          )}
          {alertMessage && (
            <HStack spacing={1} mt={1}>
              <WarningTwoIcon color="red.500" boxSize={3} />
              <Text fontSize="xs" color="red.600" fontWeight="medium" textAlign="left">
                {alertMessage}
              </Text>
            </HStack>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default MetricTile;
